import { Pressable, Text, View } from "react-native";
import { PresetTheme } from "./preset-themes-section";
import { TickCircle, Edit2 } from "iconsax-react-nativejs";
import { useState } from "react";
import { useTheme } from "@/src/styles/theme-provider";
import ColorPickerModal from "./color-picker";

type ColorCircleProps = Readonly<{
  color: string;
  size?: number;
}>;

const ColorCircle = ({ color, size = 40 }: ColorCircleProps) => (
  <View
    style={{
      width: size,
      height: size,
      borderRadius: size / 2,
      backgroundColor: color,
    }}
  />
);

type ThemeTileProps = Readonly<{
  theme: PresetTheme;
  isSelected: boolean;
  isSaved: boolean;
  onPress: () => void;
  onColorEdit?: (hex: string) => void;
}>;

const ThemeTile = ({
  theme,
  isSelected,
  isSaved,
  onPress,
  onColorEdit,
}: ThemeTileProps) => {
  const { themeVars } = useTheme();
  const [showPicker, setShowPicker] = useState(false);
  const [themeColor, setThemeColor] = useState(theme.color);

  const handleApply = (hex: string) => {
    setThemeColor(hex);
    onColorEdit?.(hex);
  };

  return (
    <>
      <View className="min-w-32 md:min-w-64">
        <Pressable
          onPress={onPress}
          className={`relative flex-col md:flex-row items-center justify-between rounded-full border py-3 px-3 drop-shadow-sm active:opacity-90 web:hover:border-ash-1
          ${
            isSelected
              ? "border-primary bg-primary-2 hover:bg-primary-2"
              : isSaved
                ? "border-primary bg-chalk hover:bg-ash-3"
                : "border-ash-3 bg-chalk hover:bg-ash-3"
          }
        `}
        >
          <View className="flex-col md:flex-row items-center gap-2">
            {onColorEdit ? (
              <Pressable
                onPress={(e) => {
                  e.stopPropagation();
                  setShowPicker(true);
                }}
                className="relative active:opacity-70 rounded-full hover:bg-ash-3"
                hitSlop={6}
              >
                <ColorCircle color={themeColor} size={38} />
                {/* Small edit badge */}
                <View className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-white dark:bg-neutral-800 items-center justify-center shadow">
                  <Edit2
                    size={9}
                    color={themeVars["--color-ash-1"] as string}
                    variant="Bold"
                  />
                </View>
              </Pressable>
            ) : (
              <ColorCircle color={themeColor} size={38} />
            )}

            <Text
              numberOfLines={1}
              adjustsFontSizeToFit
              minimumFontScale={0.75}
              className={`text-xs text-center px-1
            ${isSelected ? "font-semibold text-primary" : "text-ash-2"}
          `}
            >
              {theme.name}
            </Text>
          </View>

          {isSaved && (
            <TickCircle
              size={16}
              color={
                isSelected
                  ? (themeVars["--color-primary-default"] as string)
                  : (themeVars["--color-ash-1"] as string)
              }
            />
          )}
        </Pressable>
      </View>

      {onColorEdit && (
        <ColorPickerModal
          visible={showPicker}
          initialColor={themeColor}
          onClose={() => setShowPicker(false)}
          onApply={handleApply}
        />
      )}
    </>
  );
};

export default ThemeTile;
